import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';

interface DinoRunnerProps {
  onBack: () => void;
}

interface Obstacle {
  id: number;
  x: number;
  height: number;
}

const DinoRunner: React.FC<DinoRunnerProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [dinoY, setDinoY] = useState(0);
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const gameLoopRef = useRef<number>();
  const obstacleIdRef = useRef(0);

  const GROUND_HEIGHT = 60;
  const DINO_SIZE = 50;
  const JUMP_HEIGHT = 180;
  const GAME_SPEED = 4;

  const jump = useCallback(() => {
    if (!isJumping && isPlaying && !gameOver) {
      setIsJumping(true);
      setDinoY(JUMP_HEIGHT);
      setTimeout(() => {
        setDinoY(0);
        setTimeout(() => setIsJumping(false), 300);
      }, 600);
    }
  }, [isJumping, isPlaying, gameOver]);

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setDinoY(0);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setDinoY(0);
  };

  // Handle any key press or click for jump
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isPlaying && !gameOver) {
          startGame();
        } else {
          jump();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [jump, isPlaying, gameOver]);

  // Game loop
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const gameLoop = () => {
      setObstacles(prev => {
        const updated = prev
          .map(obs => ({ ...obs, x: obs.x - GAME_SPEED }))
          .filter(obs => obs.x > -50);

        // Collision detection
        const dinoLeft = 80;
        const dinoRight = dinoLeft + DINO_SIZE;
        const dinoBottom = GROUND_HEIGHT + dinoY;
        const dinoTop = dinoBottom + DINO_SIZE;

        for (const obs of updated) {
          const obsLeft = obs.x;
          const obsRight = obs.x + 30;
          const obsTop = GROUND_HEIGHT + obs.height;

          if (
            dinoRight > obsLeft &&
            dinoLeft < obsRight &&
            dinoBottom < obsTop
          ) {
            setGameOver(true);
            setIsPlaying(false);
            return updated;
          }
        }

        return updated;
      });

      setScore(prev => prev + 1);
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [isPlaying, gameOver, dinoY]);

  // Spawn obstacles
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const spawnInterval = setInterval(() => {
      if (Math.random() > 0.3) {
        setObstacles(prev => [
          ...prev,
          {
            id: obstacleIdRef.current++,
            x: 800,
            height: 40 + Math.random() * 30,
          },
        ]);
      }
    }, 1500);

    return () => clearInterval(spawnInterval);
  }, [isPlaying, gameOver]);

  const handleGameAreaClick = () => {
    if (!isPlaying && !gameOver) {
      startGame();
    } else if (isPlaying) {
      jump();
    }
  };

  const handleGameAreaKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleGameAreaClick();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-slate-900 flex flex-col overflow-hidden z-[100]"
      dir="ltr"
      role="button"
      tabIndex={0}
      onClick={handleGameAreaClick}
      onKeyDown={handleGameAreaKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-800/50">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
          variant="outline"
          size="lg"
          className="bg-slate-700 border-cyan-400 text-cyan-400 hover:bg-cyan-400/20"
        >
          <ArrowLeft className="mr-2 h-6 w-6" />
          {t("back")}
        </Button>
        <div className="text-4xl font-bold text-yellow-400">
          {t("score")}: {score}
        </div>
      </div>

      {/* Game Area */}
      <div className="flex-1 relative">
        {/* Ground */}
        <div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cyan-600 to-cyan-400"
          style={{ height: GROUND_HEIGHT }}
        />

        {/* Dino */}
        <motion.div
          className="absolute bg-yellow-400 rounded-lg shadow-lg shadow-yellow-400/50"
          style={{
            width: DINO_SIZE,
            height: DINO_SIZE,
            left: 80,
            bottom: GROUND_HEIGHT + dinoY,
          }}
          animate={{ y: isJumping ? -10 : 0 }}
        >
          <div className="w-full h-full flex items-center justify-center text-2xl">
            🦖
          </div>
        </motion.div>

        {/* Obstacles */}
        {obstacles.map(obs => (
          <div
            key={obs.id}
            className="absolute bg-magenta-500 rounded-t-lg"
            style={{
              width: 30,
              height: obs.height,
              left: obs.x,
              bottom: GROUND_HEIGHT,
              backgroundColor: '#d946ef',
              boxShadow: '0 0 20px #d946ef',
            }}
          />
        ))}

        {/* Start/Game Over Screen */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
            <div className="text-center">
              {gameOver ? (
                <>
                  <h2 className="text-5xl font-bold text-red-400 mb-4">{t("gameOver")}</h2>
                  <p className="text-3xl text-yellow-400 mb-8">{t("score")}: {score}</p>
                </>
              ) : (
                <h2 className="text-5xl font-bold text-cyan-400 mb-8">{t("dinoRunner")}</h2>
              )}
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  if (gameOver) {
                    resetGame();
                  } else {
                    startGame();
                  }
                }}
                size="lg"
                className="bg-yellow-400 text-slate-900 hover:bg-yellow-300 text-2xl px-8 py-6"
              >
                {gameOver ? <RotateCcw className="mr-2 h-8 w-8" /> : <Play className="mr-2 h-8 w-8" />}
                {gameOver ? t("playAgain") : t("startGame")}
              </Button>
              <p className="mt-6 text-xl text-slate-400">
                {t("dinoTapToJump")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Touch area indicator */}
      {isPlaying && (
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2">
          <div className="text-cyan-400 text-xl animate-pulse">
            👆 {t("dinoTapAnywhere")}
          </div>
        </div>
      )}
    </div>
  );
};

export default DinoRunner;
